import { apiSlice } from './apiSlice';
import { Notification, NotificationSettings } from '../../types';

/** Must match getNotifications providesTags — invalidating bare 'Notification' does not refetch LIST in RTK Query 1.9. */
export const notificationListTag = { type: 'Notification' as const, id: 'LIST' as const };

export interface GetNotificationsParams {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
}

export interface CreateNotificationRequest {
  userId: number;
  type: 'info' | 'warning' | 'error' | 'success';
  priority?: 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW';
  taskId?: number | null;
  title: string;
  message: string;
  data?: any;
  sentVia?: 'system' | 'email' | 'sms';
}

export interface UpdateNotificationSettingsRequest {
  emailEnabled: boolean;
  smsEnabled: boolean;
  notificationTypes: {
    newApplications: boolean;
    statusChanges: boolean;
    routeUpdates: boolean;
    warehouseOperations: boolean;
    documentIssues: boolean;
    delays: boolean;
  };
  schedule: 'immediate' | 'daily' | 'weekly';
}

export const notificationsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Получить уведомления пользователя
    getNotifications: builder.query<{
      notifications: Notification[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
      unreadCount: number;
    }, GetNotificationsParams>({
      query: (params) => ({
        url: '/notifications',
        params
      }),
      transformResponse: (response: any) => response.data,
      providesTags: (result) =>
        result
          ? [
              notificationListTag,
              ...result.notifications.map((n) => ({ type: 'Notification' as const, id: n.id })),
            ]
          : [notificationListTag],
    }),

    // Отметить уведомление как прочитанное
    markAsRead: builder.mutation<void, number>({
      query: (id) => ({
        url: `/notifications/${id}/read`,
        method: 'PATCH'
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Notification', id },
        notificationListTag,
      ],
    }),

    // Отметить все уведомления как прочитанные
    markAllAsRead: builder.mutation<void, void>({
      query: () => ({
        url: '/notifications/mark-all-read',
        method: 'PATCH'
      }),
      invalidatesTags: [notificationListTag],
    }),

    // Получить настройки уведомлений
    getNotificationSettings: builder.query<NotificationSettings, void>({
      query: () => '/notifications/settings',
      transformResponse: (response: any) => response.data,
      providesTags: ['NotificationSettings']
    }),

    // Обновить настройки уведомлений
    updateNotificationSettings: builder.mutation<NotificationSettings, UpdateNotificationSettingsRequest>({
      query: (settings) => ({
        url: '/notifications/settings',
        method: 'PUT',
        body: settings
      }),
      transformResponse: (response: any) => response.data,
      invalidatesTags: ['NotificationSettings']
    }),

    // Создать уведомление (только для логистов)
    createNotification: builder.mutation<Notification, CreateNotificationRequest>({
      query: (notification) => ({
        url: '/notifications',
        method: 'POST',
        body: notification
      }),
      transformResponse: (response: any) => response.data,
      invalidatesTags: [notificationListTag],
    }),

    // Удалить уведомление
    deleteNotification: builder.mutation<void, number>({
      query: (id) => ({
        url: `/notifications/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Notification', id },
        notificationListTag,
      ],
    })
  })
});

export const {
  useGetNotificationsQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useCreateNotificationMutation,
  useDeleteNotificationMutation
} = notificationsApi; 